
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  Plus, Search, X, Building2, PlusCircle, Trash2, 
  Calendar, MessageCircle, 
  DollarSign, CalendarCheck, PackagePlus, 
  MoreHorizontal, Phone, CheckCircle2, LayoutList, Trash,
  Save, MapPin, Hash, Info, UserCircle, Sparkles, Check, FileText, UserCheck,
  Mail, Target, Edit3, Pipette,
  ExternalLink
} from 'lucide-react';
import { Lead, LeadStatus, User as UserType } from './types';
import { Badge, InfoItem } from './SharedComponents';

const kanbanStyles = `
  .kanban-wrapper {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 160px);
    width: 100%;
    overflow: hidden;
  }
  
  .kanban-board-horizontal {
    flex: 1;
    overflow-x: auto;
    display: flex;
    gap: 1.5rem;
    padding: 1rem;
    padding-bottom: 2rem;
    -webkit-overflow-scrolling: touch;
    align-items: flex-start;
  }

  .kanban-column {
    width: 320px;
    min-width: 320px;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    background: #f8fafc;
    border-radius: 28px;
    border: 1px solid #f1f5f9;
    flex-shrink: 0;
    transition: background-color 0.2s;
  }
  
  .kanban-column.drag-over {
    background-color: #f1f5f9;
    border-color: #3b82f6;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }
  
  .column-scroll-area {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    min-height: 150px;
    scrollbar-width: thin;
    scrollbar-color: #e2e8f0 transparent;
  }
  .column-scroll-area::-webkit-scrollbar { width: 4px; }
  .column-scroll-area::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }

  .lead-card {
    cursor: grab;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    border-left-width: 5px !important;
    background: white;
    margin-bottom: 12px;
    user-select: none;
  }
  .lead-card:active { cursor: grabbing; transform: scale(0.98); }
  .lead-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 20px -5px rgba(0, 0, 0, 0.05);
  }

  .fixed-context-menu {
    position: fixed;
    z-index: 9999;
    background: white;
    min-width: 240px;
    border-radius: 24px;
    box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
    border: 1px solid #f1f5f9;
    padding: 8px;
    animation: menuIn 0.15s ease-out;
  }

  @keyframes menuIn {
    from { opacity: 0; transform: scale(0.95) translateY(-5px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }

  .status-overdue { border-left-color: #ef4444 !important; }
  .status-today { border-left-color: #10b981 !important; }
  .status-future { border-left-color: #3b82f6 !important; }
`;

interface KanbanColumn {
  id: string;
  title: string;
  color: string;
}

export const LeadsPage = ({ users, leads, columns: initialColumns, onUpdateColumns, onSelectLead, onConvertLead, onStartProposal, onCreateLead, onUpdateLead, onDeleteLead }: any) => {
  const [search, setSearch] = useState('');
  const [isNewLeadModalOpen, setIsNewLeadModalOpen] = useState(false);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<KanbanColumn | null>(null);
  
  const [activeLeadMenu, setActiveLeadMenu] = useState<{leadId: string, x: number, y: number} | null>(null);
  const [activeColMenu, setActiveColMenu] = useState<{colId: string, x: number, y: number} | null>(null);
  
  const [dragOverColId, setDragOverColId] = useState<string | null>(null);
  const [isConfirmingDeleteLead, setIsConfirmingDeleteLead] = useState(false);
  const [selectedLeadForReservation, setSelectedLeadForReservation] = useState<Lead | null>(null);
  
  const [columns, setColumns] = useState<KanbanColumn[]>(() => {
    if (initialColumns && initialColumns.length > 0) return initialColumns;
    return [
      { id: 'lead-novo', title: 'Leads Novos', color: '#3b82f6' },
      { id: LeadStatus.PROPOSTA, title: 'Proposta', color: '#06b6d4' },
      { id: 'em-negociacao', title: 'Negociação', color: '#f59e0b' },
      { id: LeadStatus.CUSTOMER, title: 'Concluídos', color: '#10b981' },
      { id: LeadStatus.LOST, title: 'Perdidos', color: '#ef4444' },
    ];
  });

  useEffect(() => {
    if (initialColumns && initialColumns.length > 0) {
      const timer = setTimeout(() => {
        setColumns(prev => {
          if (JSON.stringify(prev) === JSON.stringify(initialColumns)) return prev;
          return initialColumns;
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [initialColumns]);

  const filteredLeads = useMemo(() => {
    return leads.filter((l: Lead) => 
      l.name.toLowerCase().includes(search.toLowerCase()) || 
      l.company.toLowerCase().includes(search.toLowerCase())
    );
  }, [leads, search]);

  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    e.dataTransfer.setData('leadId', leadId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, colId: string) => {
    e.preventDefault();
    setDragOverColId(colId);
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    setDragOverColId(null);
    const leadId = e.dataTransfer.getData('leadId');
    if (!leadId) return;

    const lead = leads.find((l: Lead) => l.id === leadId);
    if (lead) {
      onUpdateLead?.(leadId, { ...lead, status: targetStatus });
    }
  };

  const handleLeadAction = (leadId: string, action: string) => {
    const lead = leads.find((l: Lead) => l.id === leadId);
    if (!lead) return;
    
    switch(action) {
      case 'win': onConvertLead(leadId); break;
      case 'fail': onUpdateLead?.(leadId, { ...lead, status: LeadStatus.LOST }); break;
      case 'proposta': onUpdateLead?.(leadId, { ...lead, status: LeadStatus.PROPOSTA }); onStartProposal(); break;
      case 'details': onSelectLead(lead); break;
      case 'reserve': setSelectedLeadForReservation(lead); break;
      case 'whatsapp': window.open(`https://wa.me/55${lead.phone.replace(/\D/g, '')}`, '_blank'); break;
      case 'delete': onDeleteLead?.(leadId); break;
    }
    setActiveLeadMenu(null);
    setIsConfirmingDeleteLead(false);
  };

  const handleColumnAction = (colId: string, action: string) => {
    const col = columns.find(c => c.id === colId);
    if (!col) return;

    if (action === 'edit') {
      setEditingColumn(col);
      setIsColumnModalOpen(true);
    } else if (action === 'delete') {
      if (filteredLeads.some((l: Lead) => l.status === colId)) {
        alert("Mova os leads antes de excluir esta coluna.");
      } else {
        const updated = columns.filter(c => c.id !== colId);
        setColumns(updated);
        onUpdateColumns?.(updated);
      }
    }
    setActiveColMenu(null);
  };

  return (
    <div className="h-full flex flex-col space-y-4">
      <style>{kanbanStyles}</style>
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-4 shrink-0">
        <div>
          <h1 className="text-3xl font-black text-slate-800 uppercase tracking-tighter italic leading-none">Fluxo Comercial</h1>
          <p className="text-[10px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-2 mt-2">
            <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse"></div> Pipeline Core Sincronizado
          </p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1 md:w-64 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-primary transition-colors" size={20} />
            <input 
              placeholder="Pesquisar leads..." 
              className="w-full pl-12 pr-6 py-3.5 bg-white border border-slate-200 rounded-2xl outline-none font-bold text-sm text-slate-700 shadow-sm focus:ring-4 focus:ring-primary/5 transition-all"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button 
            onClick={() => { setEditingColumn(null); setIsColumnModalOpen(true); }}
            className="bg-white border-2 border-slate-200 text-slate-500 hover:text-primary hover:border-primary px-5 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 shadow-sm transition-all active:scale-95 shrink-0"
          >
            <LayoutList size={18} /> Nova Etapa
          </button>
          <button onClick={() => setIsNewLeadModalOpen(true)} className="bg-slate-900 text-white px-6 py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl hover:bg-black active:scale-95 transition-all shrink-0">
            <Plus size={18} strokeWidth={3} /> Criar Lead
          </button>
        </div>
      </div>

      <div className="kanban-wrapper">
        <div className="kanban-board-horizontal scrollbar-hide">
          {columns.map(col => (
            <div 
              key={col.id} 
              className={`kanban-column ${dragOverColId === col.id ? 'drag-over' : ''}`}
              onDragOver={e => handleDragOver(e, col.id)}
              onDragLeave={() => setDragOverColId(null)}
              onDrop={e => handleDrop(e, col.id)}
            >
              <div className="p-5 flex items-center justify-between bg-white/50 backdrop-blur rounded-t-[28px] border-b border-slate-100 shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: col.color }}></div>
                  <h3 className="text-[11px] font-black uppercase text-slate-600 tracking-[0.1em] truncate">{col.title}</h3>
                </div>
                <div className="flex items-center gap-2">
                   <Badge className="bg-white text-slate-400 border-slate-100 font-black text-[10px] px-2.5 py-0.5 shadow-sm">
                    {filteredLeads.filter((l: Lead) => l.status === col.id).length}
                   </Badge>
                   <button 
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setActiveColMenu({ colId: col.id, x: rect.left, y: rect.bottom + 10 });
                    }}
                    className="p-1.5 text-slate-300 hover:text-slate-600 rounded-lg"
                   >
                     <MoreHorizontal size={16} />
                   </button>
                </div>
              </div>

              <div className="column-scroll-area">
                {filteredLeads.filter((l: Lead) => l.status === col.id).map((lead: Lead) => (
                  <LeadCard 
                    key={lead.id} 
                    lead={lead} 
                    users={users}
                    onDragStart={handleDragStart} 
                    onOpenMenu={(x: number, y: number) => setActiveLeadMenu({leadId: lead.id, x, y})} 
                    onAction={handleLeadAction}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Portal de Menu para LEADS */}
      {activeLeadMenu && (
        <>
          <div className="fixed inset-0 z-[9998] bg-black/5 backdrop-blur-[2px]" onClick={() => {setActiveLeadMenu(null); setIsConfirmingDeleteLead(false);}}></div>
          <div className="fixed-context-menu" style={{ top: Math.min(activeLeadMenu.y, window.innerHeight - 380), left: Math.min(activeLeadMenu.x, window.innerWidth - 260) }}>
             <div className="p-4 border-b border-slate-50 flex justify-between items-center mb-1">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Opções do Lead</span>
                <button onClick={() => setActiveLeadMenu(null)} className="text-slate-300 hover:text-red-500"><X size={16} /></button>
             </div>
             <div className="space-y-1">
                <MenuBtn icon={CheckCircle2} label="Abrir Perfil" onClick={() => handleLeadAction(activeLeadMenu.leadId, 'details')} />
                <MenuBtn icon={PlusCircle} label="Gerar Orçamento" onClick={() => handleLeadAction(activeLeadMenu.leadId, 'proposta')} />
                <MenuBtn icon={CalendarCheck} label="Reservar Data" color="text-cyan-600" onClick={() => handleLeadAction(activeLeadMenu.leadId, 'reserve')} />
                <div className="h-[1px] bg-slate-50 mx-2 my-1"></div>
                <MenuBtn icon={PackagePlus} label="Converter em Venda" color="text-green-600" onClick={() => handleLeadAction(activeLeadMenu.leadId, 'win')} />
                <MenuBtn icon={Trash2} label="Lead Perdido" color="text-red-400" onClick={() => handleLeadAction(activeLeadMenu.leadId, 'fail')} />
                <div className="h-[1px] bg-slate-50 mx-2 my-1"></div>
                {isConfirmingDeleteLead ? (
                  <button onClick={() => handleLeadAction(activeLeadMenu.leadId, 'delete')} className="w-full flex items-center gap-3 p-4 bg-red-500 text-white rounded-2xl font-black uppercase text-[10px] animate-pulse shadow-lg"><Trash size={16} /> CONFIRMAR EXCLUSÃO?</button>
                ) : (
                  <MenuBtn icon={Trash} label="Excluir Registro" color="text-red-600" onClick={() => setIsConfirmingDeleteLead(true)} />
                )}
             </div>
          </div>
        </>
      )}

      {/* Portal de Menu para COLUNAS */}
      {activeColMenu && (
        <>
          <div className="fixed inset-0 z-[9998] bg-black/5" onClick={() => setActiveColMenu(null)}></div>
          <div className="fixed-context-menu" style={{ top: activeColMenu.y, left: activeColMenu.x }}>
             <div className="p-4 border-b border-slate-50 flex justify-between items-center mb-1">
                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Configurar Coluna</span>
                <button onClick={() => setActiveColMenu(null)} className="text-slate-300 hover:text-red-500"><X size={16} /></button>
             </div>
             <div className="space-y-1">
                <MenuBtn icon={Edit3} label="Editar Nome/Cor" onClick={() => handleColumnAction(activeColMenu.colId, 'edit')} />
                <div className="h-[1px] bg-slate-50 mx-2 my-1"></div>
                <MenuBtn icon={Trash2} label="Excluir Coluna" color="text-red-600" onClick={() => handleColumnAction(activeColMenu.colId, 'delete')} />
             </div>
          </div>
        </>
      )}

      {isNewLeadModalOpen && (
        <NewLeadModal 
          users={users} 
          columns={columns} 
          onClose={() => setIsNewLeadModalOpen(false)} 
          onSave={(l: Partial<Lead>) => { onCreateLead?.(l); setIsNewLeadModalOpen(false); }} 
        />
      )}

      {isColumnModalOpen && (
        <ColumnModal 
          column={editingColumn}
          onClose={() => setIsColumnModalOpen(false)}
          onSave={(newCol: KanbanColumn) => {
            let updated;
            if (editingColumn) {
              updated = columns.map(c => c.id === editingColumn.id ? newCol : c);
            } else {
              updated = [...columns, newCol];
            }
            setColumns(updated);
            onUpdateColumns?.(updated);
            setIsColumnModalOpen(false);
          }}
        />
      )}

      {selectedLeadForReservation && (
        <DateSelectionModal 
          lead={selectedLeadForReservation} 
          onClose={() => setSelectedLeadForReservation(null)} 
          onConfirm={(date: string) => {
            onUpdateLead?.(selectedLeadForReservation.id, { ...selectedLeadForReservation, tags: [...selectedLeadForReservation.tags, 'PRÉ-RESERVA'], followUpDate: date });
            setSelectedLeadForReservation(null);
          }} 
        />
      )}
    </div>
  );
};

const LeadCard = ({ lead, users, onDragStart, onOpenMenu, onAction }: any) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  
  const assignedUser = useMemo(() => {
    return users.find((u: any) => u.id === lead.assignedTo || u.name === lead.assignedTo);
  }, [users, lead.assignedTo]);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      onOpenMenu(rect.left - 200, rect.bottom + 10);
    }
  };

  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const dateStatus = !lead.followUpDate ? 'future' : lead.followUpDate === todayStr ? 'today' : lead.followUpDate < todayStr ? 'overdue' : 'future';
  const statusClasses = { overdue: 'status-overdue', today: 'status-today', future: 'status-future' };

  return (
    <div 
      draggable
      onDragStart={e => onDragStart(e, lead.id)}
      className={`lead-card p-5 rounded-[22px] border border-slate-100 bg-white shadow-sm flex flex-col gap-4 ${statusClasses[dateStatus as keyof typeof statusClasses]}`}
      onClick={(e) => { e.stopPropagation(); onAction(lead.id, 'details'); }}
    >
      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-1">
           <p className="text-[13px] font-black text-emerald-600 tracking-tight leading-none">R$ {lead.value.toLocaleString('pt-BR')}</p>
           <p className="text-[8px] font-bold text-slate-300 uppercase tracking-widest">{lead.source?.toUpperCase() || 'MANUAL'}</p>
        </div>
        <Badge className="bg-slate-50 text-slate-400 border-none text-[8px] font-bold px-2 py-0.5">#{lead.id.slice(-4)}</Badge>
      </div>

      <div className="min-h-[48px] space-y-1">
        <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight leading-tight line-clamp-2">{lead.name}</h4>
        <div className="flex items-center gap-1.5 opacity-60">
          <Building2 size={12} className="text-slate-400" />
          <p className="text-[9px] font-bold text-slate-400 uppercase truncate tracking-widest">{lead.company}</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-slate-50">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-xl bg-slate-900 text-white flex items-center justify-center text-[9px] font-black shadow-lg italic">
            {assignedUser?.name.charAt(0) || 'G'}
          </div>
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-tight">{assignedUser?.name.split(' ')[0] || 'Gestor'}</span>
        </div>

        <div className="flex items-center gap-1.5">
           <button 
            onClick={(e) => { e.stopPropagation(); onAction(lead.id, 'whatsapp'); }}
            className="w-9 h-9 bg-green-500 text-white rounded-2xl shadow-lg shadow-green-100 hover:scale-105 active:scale-95 transition-all flex items-center justify-center"
           >
             <MessageCircle size={18} fill="currentColor" />
           </button>
           <button 
             ref={triggerRef}
             onClick={handleMenuClick}
             className="w-9 h-9 bg-slate-50 text-slate-300 hover:text-primary hover:bg-white border border-transparent hover:border-slate-100 rounded-2xl transition-all flex items-center justify-center"
           >
             <MoreHorizontal size={18} />
           </button>
        </div>
      </div>
    </div>
  );
};

const ColumnModal = ({ column, onClose, onSave }: any) => {
  const [formData, setFormData] = useState(() => ({
    title: column?.title || '',
    color: column?.color || '#3b82f6',
    id: column?.id || `col-${Date.now()}`
  }));

  const colors = ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#64748b'];

  return (
    <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md z-[1000] flex items-center justify-center p-6 animate-in fade-in duration-300">
       <div className="bg-white rounded-[40px] w-full max-w-lg shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] overflow-hidden animate-in zoom-in-95 border border-white/20">
          
          <div className="p-10 bg-[#0f172a] text-white flex justify-between items-center shrink-0 relative overflow-hidden">
             <div className="absolute top-0 right-0 p-10 opacity-5 rotate-12 scale-150 pointer-events-none">
                <LayoutList size={200} />
             </div>
             <div className="relative z-10">
                <h2 className="text-3xl font-black uppercase italic tracking-tighter leading-none">{column ? 'Editar Etapa' : 'Nova Coluna'}</h2>
                <p className="text-[10px] font-black text-primary uppercase tracking-[0.4em] mt-3">Funil de Vendas Inteligente</p>
             </div>
             <button onClick={onClose} className="p-3 bg-white/5 hover:bg-red-500/20 hover:text-red-500 rounded-2xl transition-all relative z-10 active:scale-90"><X size={32} /></button>
          </div>

          <div className="p-10 space-y-10 bg-white">
             <div className="space-y-3">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Nome da Etapa</label>
                <div className="relative group">
                   <Target className="absolute left-5 top-1/2 -translate-y-1/2 text-primary" size={20} />
                   <input 
                    required
                    autoFocus
                    className="w-full bg-slate-50 border border-slate-100 p-5 rounded-[24px] outline-none font-black text-secondary text-xl tracking-tight focus:bg-white focus:ring-8 focus:ring-primary/5 transition-all shadow-inner placeholder:text-gray-200"
                    placeholder="Ex: Qualificação"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                   />
                </div>
             </div>

             <div className="space-y-6">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4 flex items-center gap-2">
                   <Pipette size={14} className="text-primary" /> Cor Visual de Destaque
                </label>
                <div className="grid grid-cols-4 gap-4">
                   {colors.map(c => (
                     <button 
                       key={c}
                       type="button"
                       onClick={() => setFormData({...formData, color: c})}
                       className={`w-full h-14 rounded-[20px] transition-all border-4 ${
                        formData.color === c 
                          ? 'border-white ring-4 ring-primary/20 scale-105 shadow-xl' 
                          : 'border-transparent opacity-60 hover:opacity-100'
                       }`}
                       style={{ backgroundColor: c }}
                     />
                   ))}
                </div>
             </div>

             <div className="pt-6">
                <button 
                  onClick={() => onSave(formData)}
                  disabled={!formData.title}
                  className="w-full py-6 bg-primary text-white rounded-[32px] font-black uppercase text-xs tracking-[0.2em] shadow-2xl shadow-primary/30 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-4 group"
                >
                   <Save size={22} className="group-hover:rotate-12 transition-transform" />
                   {column ? 'Salvar Alterações' : 'Criar Coluna Agora'}
                </button>
                <button onClick={onClose} className="w-full py-4 mt-2 text-[9px] font-black text-gray-300 uppercase tracking-widest hover:text-secondary transition-colors">Cancelar Operação</button>
             </div>
          </div>
       </div>
    </div>
  );
};

const MenuBtn = ({ icon: Icon, label, color = "text-slate-600", onClick }: any) => (
  <button 
    onClick={onClick}
    className={`w-full text-left px-4 py-3.5 hover:bg-slate-50 flex items-center gap-4 transition-colors ${color} rounded-2xl group active:scale-[0.98]`}
  >
    <Icon size={18} className="group-hover:scale-110 transition-transform shrink-0" />
    <span className="text-[11px] font-black uppercase tracking-widest">{label}</span>
  </button>
);

const NewLeadModal = ({ users, columns, onClose, onSave }: any) => {
  const [formData, setFormData] = useState({ 
    name: '', company: '', phone: '', email: '', value: '', 
    cnpj: '', cpf: '',
    status: columns[0]?.id || 'lead-novo', assignedTo: users[0]?.id || '',
    source: 'Instagram',
    cep: '', logradouro: '', numero: '', bairro: '', cidade: '', uf: 'SP'
  });

  return (
    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
      <div className="bg-white rounded-[40px] w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 flex flex-col max-h-[95vh] border border-white/20">
         <div className="p-8 bg-slate-900 text-white flex justify-between items-center shrink-0">
            <div className="flex items-center gap-4">
               <div className="p-3 bg-primary rounded-2xl shadow-lg"><Sparkles size={28} /></div>
               <div>
                  <h2 className="text-2xl font-black uppercase tracking-tight italic">Novo Prospect Comercial</h2>
                  <p className="text-[10px] font-bold text-primary uppercase tracking-widest mt-1">Lançamento no Funil Ativo</p>
               </div>
            </div>
            <button onClick={onClose} className="p-3 bg-white/10 hover:bg-red-500/20 hover:text-red-500 rounded-2xl transition-all active:scale-90"><X size={28} /></button>
         </div>

         <form className="p-8 md:p-10 space-y-10 overflow-y-auto scrollbar-hide flex-1" onSubmit={e => { e.preventDefault(); onSave({ id: `temp-lead-${Date.now()}`, ...formData, value: parseFloat(formData.value || '0'), tags: [], lastContact: new Date().toISOString(), createdAt: new Date().toISOString() }); }}>
            
            <section className="space-y-6">
               <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-3 border-b border-slate-100 pb-3 italic">
                  <UserCircle size={16} /> Identificação do Prospect
               </h3>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                 <ModalInput label="Nome Completo" value={formData.name} onChange={(v:any) => setFormData({...formData, name: v})} required placeholder="Ex: João da Silva" />
                 <ModalInput label="Empresa / Grupo" value={formData.company} onChange={(v:any) => setFormData({...formData, company: v})} required placeholder="Ex: Pepsico Brasil" />
                 <ModalInput label="WhatsApp Profissional" value={formData.phone} onChange={(v:any) => setFormData({...formData, phone: v})} required placeholder="(00) 00000-0000" />
                 <ModalInput label="E-mail Comercial" type="email" value={formData.email} onChange={(v:any) => setFormData({...formData, email: v})} placeholder="contato@empresa.com.br" />
               </div>
               <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                 <ModalInput label="CNPJ (Empresa)" value={formData.cnpj} onChange={(v:any) => setFormData({...formData, cnpj: v})} placeholder="00.000.000/0001-00" />
                 <ModalInput label="CPF (Responsável)" value={formData.cpf} onChange={(v:any) => setFormData({...formData, cpf: v})} placeholder="000.000.000-00" />
               </div>
            </section>

            <section className="space-y-6">
               <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-3 border-b border-slate-100 pb-3 italic">
                  <MapPin size={16} /> Localização do Evento (Logística)
               </h3>
               <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Corrected property from zip to cep to match formData state and avoid data mismatch */}
                  <ModalInput label="CEP" value={formData.cep} onChange={(v:any) => setFormData({...formData, cep: v})} placeholder="00000-000" />
                  <div className="col-span-2 md:col-span-3">
                    <ModalInput label="Logradouro" value={formData.logradouro} onChange={(v:any) => setFormData({...formData, logradouro: v})} placeholder="Rua, Avenida..." />
                  </div>
                  <ModalInput label="Número" value={formData.numero} onChange={(v:any) => setFormData({...formData, numero: v})} placeholder="123" />
                  <ModalInput label="Bairro" value={formData.bairro} onChange={(v:any) => setFormData({...formData, bairro: v})} placeholder="Ex: Campolim" />
                  <ModalInput label="Cidade" value={formData.cidade} onChange={(v:any) => setFormData({...formData, cidade: v})} placeholder="Sorocaba" />
                  <ModalInput label="UF" value={formData.uf} onChange={(v:any) => setFormData({...formData, uf: v.toUpperCase()})} maxLength={2} center />
               </div>
            </section>

            <section className="space-y-6">
               <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-3 border-b border-slate-100 pb-3 italic">
                  <DollarSign size={16} /> Parâmetros de Venda
               </h3>
               <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="md:col-span-1">
                    <ModalInput label="Faturamento Estimado (R$)" type="number" value={formData.value} onChange={(v:any) => setFormData({...formData, value: v})} required placeholder="0,00" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Fonte</label>
                    <select className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl font-bold text-slate-700 outline-none cursor-pointer shadow-inner" value={formData.source} onChange={e => setFormData({...formData, source: e.target.value})}>
                       <option>Instagram</option><option>Facebook</option><option>Google</option><option>Indicação</option><option>Manual</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Etapa Inicial</label>
                    <select className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl font-bold text-slate-700 outline-none cursor-pointer shadow-inner" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                       {columns.map((c: any) => <option key={c.id} value={c.id}>{c.title}</option>)}
                    </select>
                  </div>
               </div>
            </section>

            <button type="submit" className="w-full py-6 bg-primary text-white rounded-[28px] font-black uppercase text-sm tracking-widest shadow-xl shadow-primary/30 mt-8 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-4">
              <CheckCircle2 size={24} strokeWidth={3} /> Lançar no Funil Comercial
            </button>
         </form>
      </div>
    </div>
  );
};

const ModalInput = ({ label, value, onChange, type = "text", required, maxLength, placeholder, center }: any) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{label}{required && <span className="text-primary ml-1">*</span>}</label>
    <input 
      type={type} 
      maxLength={maxLength} 
      className={`w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-bold text-slate-700 focus:bg-white focus:ring-4 focus:ring-primary/5 border-transparent focus:border-primary/20 transition-all text-sm shadow-inner placeholder:text-gray-300 ${center ? 'text-center' : ''}`} 
      value={value} 
      onChange={e => onChange(e.target.value)} 
      required={required}
      placeholder={placeholder}
    />
  </div>
);

const DateSelectionModal = ({ onClose, onConfirm }: any) => {
  const [date, setDate] = useState('');
  return (
    <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-sm z-[1100] flex items-center justify-center p-6 animate-in fade-in duration-300">
       <div className="bg-white rounded-[32px] w-full max-sm shadow-2xl overflow-hidden animate-in zoom-in-95 border-4 border-white">
          <div className="p-6 bg-cyan-500 text-white flex flex-col items-center gap-4 relative overflow-hidden">
             <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12 scale-150 pointer-events-none"><Calendar size={100} /></div>
             <div className="p-3.5 bg-white/20 rounded-2xl backdrop-blur-md">
                <CalendarCheck size={32} />
             </div>
             <div className="text-center space-y-0.5">
                <h3 className="text-xl font-black uppercase tracking-tight italic leading-none">Bloqueio de Data</h3>
                <p className="text-[8px] font-black text-white/60 uppercase tracking-widest">Reserva de Infraestrutura</p>
             </div>
          </div>
          <div className="p-6 space-y-6">
             <div className="space-y-2 text-center">
                <label className="text-[9px] font-black text-gray-400 uppercase tracking-[0.4em]">Dia do Evento</label>
                <input type="date" className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl outline-none font-black text-secondary text-xl text-center shadow-inner focus:bg-white focus:ring-4 focus:ring-cyan-500/5 transition-all" value={date} onChange={e => setDate(e.target.value)} />
             </div>
             <button onClick={() => onConfirm(date)} disabled={!date} className="w-full py-4 bg-cyan-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-cyan-100 active:scale-95 transition-all flex items-center justify-center gap-3 hover:brightness-110">
                <Check size={18} strokeWidth={4} /> Confirmar Reserva
             </button>
             <button onClick={onClose} className="w-full text-[9px] font-black text-slate-300 uppercase tracking-widest hover:text-red-400 transition-colors">Cancelar</button>
          </div>
       </div>
    </div>
  );
};

export const LeadDetailsModal = ({ lead, users, onClose, onConvert, onStartProposal, onUpdateNotes }: { lead: Lead, users: UserType[], onClose: () => void, onConvert: () => void, onStartProposal: () => void, onUpdateNotes: (notes: string) => void }) => {
  const [notes, setNotes] = useState(lead.description || '');

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[2000] flex justify-end animate-in fade-in duration-300 overflow-hidden">
      <div className="bg-white h-full w-full max-w-2xl shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 border-l border-white/20">
        
        <div className="p-8 bg-[#0f172a] text-white flex flex-col shrink-0 relative overflow-hidden gap-6">
          <div className="absolute top-0 right-0 p-8 opacity-[0.03] rotate-12 scale-150 pointer-events-none"><FileText size={200} /></div>
          
          <div className="flex justify-between items-start relative z-10 w-full">
             <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center text-white text-3xl font-black shadow-2xl italic shrink-0 border-2 border-white/10">
                   {lead.name.charAt(0)}
                </div>
                <div>
                   <Badge className="bg-primary text-white border-none px-3 py-1 font-black text-[9px] uppercase tracking-widest">PERFIL DO PARCEIRO</Badge>
                   <p className="text-[9px] font-bold text-white/30 uppercase mt-1">ID: {lead.id.slice(-6)}</p>
                </div>
             </div>
             <button onClick={onClose} className="p-3 bg-white/5 hover:bg-red-500/20 hover:text-red-500 rounded-2xl transition-all border border-white/10 shadow-2xl active:scale-90"><X size={24} /></button>
          </div>

          <div className="relative z-10">
             <h2 className="text-3xl font-black uppercase tracking-tight italic leading-none truncate">{lead.name}</h2>
             <p className="text-primary font-black uppercase text-xs tracking-[0.3em] mt-3 flex items-center gap-2">
                <Building2 size={14} /> {lead.company}
             </p>
          </div>
        </div>

        <div className="p-8 space-y-10 overflow-y-auto scrollbar-hide flex-1 bg-[#fcfdfe]">
           {/* Seção 1: Dados do Prospect 360º */}
           <section className="space-y-6">
              <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-3 border-b border-slate-100 pb-4 italic">
                 <UserCircle size={20} /> Identificação Fiscal & Jurídica
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 <InfoItem label="Responsável Comercial" value={lead.name} icon={UserCheck} />
                 <InfoItem label="WhatsApp Direto" value={lead.phone} icon={Phone} className="text-green-600" />
                 <InfoItem label="CNPJ / CPF" value={lead.cnpj || 'Não informado'} icon={Hash} />
                 <InfoItem label="E-mail Comercial" value={lead.email || '---'} icon={Mail} />
              </div>
           </section>

           {/* Seção 2: Localização Estrutural */}
           <section className="space-y-6">
              <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-3 border-b border-slate-100 pb-4 italic">
                 <MapPin size={20} /> Endereço Técnico de Operação
              </h3>
              <div className="bg-gray-50/50 p-6 rounded-3xl border border-slate-100 space-y-6 shadow-inner">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <InfoItem label="CEP de Destino" value={lead.cep || '---'} />
                    <InfoItem label="Logradouro" value={lead.logradouro || 'Não definido'} />
                    <InfoItem label="Número / Comp." value={`${lead.numero || 'S/N'}`} />
                    <InfoItem label="Bairro" value={lead.bairro || '---'} />
                    <InfoItem label="Cidade / UF" value={lead.cidade ? `${lead.cidade} - ${lead.uf}` : '---'} />
                 </div>
              </div>
           </section>

           {/* Notas de Negociação */}
           <section className="space-y-4">
              <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.4em] flex items-center gap-3 italic"><Info size={20} /> Observações Estratégicas</h3>
              <textarea 
                className="w-full bg-slate-50 border border-slate-100 p-6 rounded-[24px] outline-none font-medium text-slate-700 text-sm focus:border-primary/20 transition-all min-h-[150px] resize-none shadow-inner placeholder:text-slate-200 leading-relaxed italic"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Histórico de propostas e restrições técnicas do local..."
              />
           </section>

           <section className="space-y-6">
                <h3 className="text-[11px] font-black text-primary uppercase tracking-[0.3em] border-b border-gray-100 pb-3 flex items-center gap-2 italic">
                  <ExternalLink size={16} /> Potencial de Conversão
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-6 bg-white rounded-3xl border border-gray-100 shadow-sm flex flex-col justify-between h-32">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">Ticket Estimado</p>
                    <p className="text-2xl font-black text-secondary tracking-tighter">R$ {lead.value.toLocaleString('pt-BR')}</p>
                  </div>
                  <div className="p-6 bg-secondary text-white rounded-3xl shadow-xl flex flex-col justify-between h-32 border-4 border-white/5 relative overflow-hidden">
                    <div className="absolute top-0 right-0 p-3 opacity-5 rotate-12"><DollarSign size={64} /></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-widest leading-none relative z-10">Origem Lead</p>
                    <p className="text-xl font-black text-white relative z-10 uppercase">{lead.source}</p>
                  </div>
                </div>
              </section>
        </div>

        {/* Footer de Ações do Painel Lateral */}
        <div className="p-8 border-t border-gray-100 bg-white shrink-0 space-y-4 shadow-[0_-10px_30px_rgba(0,0,0,0.05)]">
           <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={onStartProposal}
                className="py-4 bg-primary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <FileText size={18} /> Orçamento
              </button>
              <button 
                onClick={onConvert}
                className="py-4 bg-cyan-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-cyan-500/20 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                <PackagePlus size={18} /> Venda Ganha
              </button>
           </div>
           <button 
              onClick={() => { onUpdateNotes(notes); alert('Perfil Sincronizado!'); }}
              className="w-full py-4 bg-[#0f172a] text-white rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl hover:bg-black transition-all flex items-center justify-center gap-3 active:scale-95"
           >
              <Save size={18} /> Atualizar Perfil
           </button>
        </div>
      </div>
    </div>
  );
};
